import axiosInstance from "../utils/axiosInstance";
import type {
  LandTour,
  LandTourDetail,
  LandTourFilterOptions,
  LandTourSearchResult,
  LandTourTag,
} from "../interfaces/land-tour.interface";

// GET /b2c2b/api/v1/land_tours - the B2C ground tour listing (iceholidays#4502).
// Kept separate from the B2B /api/v1/land_tours endpoint, which requires an
// authenticated agent and shows the minimum price instead of the maximum.

export interface LandTourSearchParams {
  keyword?: string;
  /** YYYY-MM-01; the API widens it to the whole month. */
  month?: string;
  /** Category enum keys: private_tour | seat_in_coach. */
  category?: string[];
  guide_languages?: string[];
  /** Inclusion column names: inc_hotel, inc_flight, … */
  inclusions?: string[];
  tag_ids?: string[];
  /** Exact trip length (the duration slider). */
  duration?: number;
  min_price?: number;
  max_price?: number;
  /** Destination facet - supported by the API, not yet surfaced in the UI. */
  location_ids?: string[];
  sortBy?: "lowest_price" | "highest_price";
  page?: number;
  per?: number;
}

const toTag = (tag: any): LandTourTag | undefined =>
  tag?.id ? { id: String(tag.id), name: tag.name, description: tag.description } : undefined;

const toNumber = (value: any): number | undefined => {
  // save_amount and price arrive as numeric strings from the JSON:API serializer.
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

// The Ground Tour show page (issue #629) has no dedicated B2C2B endpoint yet - see the
// implementation plan's "Part 1" for the readiness detail. Api::V1::LandToursController#show
// already carries every field the page needs (BE #4358 pushed the B2C fields - save_amount,
// hotel_star, child_age_ranges - into this same shared jbuilder rather than forking it), and
// Api::V1::SeriesController's `@public_access` gate is the precedent for exposing it publicly
// without a new controller. Until that one-line auth change ships, this path stays pointed at
// wherever local verification proxies it; flipping it to the real public route (or a code-based
// lookup) is a one-line change here, nowhere else.
const landTourDetailPath = (idOrCode: string | number): string => `/b2c2b/api/v1/land_tours/${idOrCode}`;

class LandToursApi {
  getLandTours(params: LandTourSearchParams): Promise<LandTourSearchResult> {
    return axiosInstance
      // Rack only parses repeated keys into an array when they carry "[]"
      // (`a=1&a=2` collapses to the last value), so every multi-select facet
      // must serialize as `inclusions[]=…&inclusions[]=…`.
      .get("/b2c2b/api/v1/land_tours", { params, paramsSerializer: { indexes: false } })
      .then((response) => {
        const rows = response.data?.data || [];
        const meta = response.data?.meta || {};

        return {
          landTours: rows.map((row: any) => this.mapLandTour(row.attributes || {})),
          total: meta.total_count ?? rows.length,
          totalPages: meta.total_pages ?? 1,
          currentPage: meta.current_page ?? 1,
          filters: this.mapFilters(meta.filters),
        };
      });
  }

  // idOrCode: the show page reads the :code param from the URL; calculate_price (once it
  // ships, iceholidays#4453) is keyed by numeric id instead, which the detail payload's own
  // `id` field then supplies - callers don't need to know which one the route used.
  getLandTour(idOrCode: string, ref?: string | null): Promise<LandTourDetail> {
    const queryParams = new URLSearchParams();
    if (ref) queryParams.set("ref", ref);

    const query = queryParams.toString();
    const apiUrl = `${landTourDetailPath(idOrCode)}${query ? `?${query}` : ""}`;

    return axiosInstance.get(apiUrl).then((response) => this.mapLandTourDetail(response.data));
  }

  private mapLandTour(attributes: any): LandTour {
    return {
      id: attributes.id,
      code: attributes.code,
      title: attributes.title,
      cnTitle: attributes.cn_title,
      category: attributes.category,
      brand: attributes.brand,
      country: attributes.country,
      days: attributes.days,
      nights: attributes.nights,
      duration: attributes.duration ?? attributes.days,
      minBooking: attributes.min_booking,
      price: toNumber(attributes.price),
      saveAmount: toNumber(attributes.save_amount) ?? 0,
      nextPaxRange: toNumber(attributes.next_pax_range),
      firstDayCity: attributes.first_day_city,
      travelDateStart: attributes.travel_date_start,
      travelDateEnd: attributes.travel_date_end,
      instantConfirmation: Boolean(attributes.instant_confirmation),
      inclusion: attributes.inclusion || {},
      imageUrl: attributes.image_url,
      images: attributes.images || [],
      primaryTag: toTag(attributes.primary_tag),
      otherTags: (attributes.other_tags || []).map(toTag).filter(Boolean) as LandTourTag[],
    };
  }

  private mapFilters(filters: any): LandTourFilterOptions {
    return {
      categories: filters?.categories || [],
      guideLanguages: filters?.guide_languages || [],
      inclusions: filters?.inclusion_options || [],
      promotions: (filters?.promotions || []).map(toTag).filter(Boolean) as LandTourTag[],
      maxDuration: filters?.max_duration || 0,
    };
  }

  // Deliberately tolerant of both the eventual public payload and today's agent-facing one
  // (verification runs against Api::V1::LandToursController#show locally, per the readiness
  // plan) - every field defaults, and nothing here throws on an absent one. What must NOT pass
  // through, from either source: dta_prices / dta_adult, which are agent commission figures
  // (cf. issue#2962) and are stripped explicitly below rather than left to a wholesale copy.
  private mapLandTourDetail(data: any): LandTourDetail {
    return {
      id: data.id,
      code: data.code,
      title: data.title,
      cnTitle: data.cn_title,
      isNew: data.is_new,
      landTourUpdatedAt: data.land_tour_updated_at,
      highlight: data.highlight,
      category: data.category,
      brand: data.brand,
      description: data.description,
      cnDescription: data.cn_description,
      termsAndConditions: data.terms_and_conditions,
      guideLanguages: data.guide_languages,
      compulsoryCharges: data.compulsory_charges,
      childPrices: data.child_prices,
      childAgeRanges: data.child_age_ranges,

      incHotel: data.inc_hotel,
      incFlight: data.inc_flight,
      incHalfBoardMeals: data.inc_half_board_meals,
      incFullBoardMeals: data.inc_full_board_meals,
      incEntranceTicket: data.inc_entrance_ticket,
      incTransportation: data.inc_transportation,
      incShoppingStop: data.inc_shopping_stop,
      incInstantConfirmation: data.inc_instant_confirmation,

      tipping: data.tipping,
      days: data.days,
      nights: data.nights,
      duration: data.duration,

      minBooking: data.min_booking,
      maxBooking: data.max_booking,
      bookingCutOffDay: data.booking_cut_off_day,
      departDay: data.depart_day,

      freeOfCharge: data.free_of_charge,
      nonMalaysianCharge: data.non_malaysian_charge,
      depositType: data.deposit_type,
      deposit: data.deposit,
      depositText: data.deposit_text,

      travelDate: data.travel_date,
      images: data.images || [],
      coverImage: data.images?.[0],
      country: data.country,

      itinerary: data.itinerary || [],
      pricingCategories: this.mapPricingCategories(data.pricing_categories),
      surcharges: data.surcharges || [],
    };
  }

  // category.price_dates is already flat here, unlike the agent-facing api/v1 endpoint's nested shape.
  private mapPricingCategories(categories: any[] = []): LandTourDetail["pricingCategories"] {
    return categories.map((category) => ({
      id: category.id,
      category: category.category,
      description: category.description,
      saveAmount: toNumber(category.save_amount) ?? 0,
      departure_dates: category.departure_dates || [],
      price_dates: (category.price_dates || [])
        .filter((window: any) => window.date)
        .map((window: any) => ({
          date: window.date,
          fullDate: window.full_date,
          cutOffDate: window.cut_off_date,
          singleSupplementPrice: Number(window.single_supplement_price) || 0,
          // Rebuilt field-by-field to drop dta_adult (agent commission) even if the source payload carries it.
          quantities: (window.quantities || []).map((tier: any) => ({
            quantity: tier.quantity || [],
            quantity_str: tier.quantity_str,
            adult: tier.adult,
            guide_type: tier.guide_type,
            child_prices: tier.child_prices || [],
          })),
          maxBookingArr: window.max_booking_arr || [],
          maxBooking: window.max_booking || 0,
        })),
    }));
  }
}

export default new LandToursApi();
